import React, { useState, useMemo } from 'react';
import './ResultadosDetallados.css';
import ModalPregunta from './ModalPregunta';

function ResultadosDetallados({ examen, respuestasUsuario, respuestasCorrectas, preguntasOrden }) {
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [preguntaModal, setPreguntaModal] = useState(null);
  // Función para determinar si una pregunta es correcta
  const esPreguntaCorrecta = (preguntaId) => {
    const respuestaUsuario = respuestasUsuario[preguntaId];
    const respuestaCorrecta = respuestasCorrectas[preguntaId];
    
    if (respuestaUsuario === undefined || respuestaUsuario === null || respuestaCorrecta === undefined) {
      return false;
    }
    
    // multiple_choice con varias correctas: respuestaCorrecta es array
    if (Array.isArray(respuestaCorrecta)) {
      const idsUsuario = Array.isArray(respuestaUsuario)
        ? respuestaUsuario
        : respuestaUsuario != null ? [respuestaUsuario] : [];
      const correctasSet = new Set(respuestaCorrecta);
      const aciertos = idsUsuario.filter((id) => correctasSet.has(id)).length;
      // Al menos una correcta seleccionada = no se marca como mala
      return aciertos > 0;
    }
    
    if (typeof respuestaCorrecta === 'object' && respuestaCorrecta !== null) {
      // Es pregunta hotspot o drag_drop
      if (typeof respuestaUsuario === 'object' && respuestaUsuario !== null && !Array.isArray(respuestaUsuario)) {
        const keysCorrectas = Object.keys(respuestaCorrecta);
        if (keysCorrectas.length === 0) return false;
        for (const key of keysCorrectas) {
          if (respuestaUsuario[key] !== respuestaCorrecta[key]) return false;
        }
        return true;
      }
      return false;
    }
    
    // Es pregunta multiple_choice una sola correcta
    return respuestaUsuario === respuestaCorrecta;
  };
  
  // Contar resultados
  const contarResultados = () => {
    let correctas = 0;
    let incorrectas = 0;
    let sinResponder = 0;
    
    preguntasOrden.forEach((preguntaId) => {
      const respuestaUsuario = respuestasUsuario[preguntaId];
      
      if (respuestaUsuario === undefined || respuestaUsuario === null) {
        sinResponder += 1;
      } else if (esPreguntaCorrecta(preguntaId)) {
        correctas += 1;
      } else {
        incorrectas += 1;
      }
    });
    
    return { correctas, incorrectas, sinResponder };
  };
  
  const conteos = contarResultados();

  // Filtrar preguntas por estado
  const preguntasFiltradas = useMemo(() => {
    if (filtroEstado === 'todos') {
      return preguntasOrden;
    }
    
    return preguntasOrden.filter((preguntaId) => {
      const respuestaUsuario = respuestasUsuario[preguntaId];
      const esCorrecta = esPreguntaCorrecta(preguntaId);
      const sinResponder = respuestaUsuario === undefined || respuestaUsuario === null;
      
      if (filtroEstado === 'correctas') return esCorrecta && !sinResponder;
      if (filtroEstado === 'incorrectas') return !esCorrecta && !sinResponder;
      if (filtroEstado === 'sin_responder') return sinResponder;
      
      return true;
    });
  }, [preguntasOrden, filtroEstado, respuestasUsuario]);
  
  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltroEstado('todos');
  };
  
  return (
    <div className="resultados-detallados-container">
      <div className="resultados-detallados-header">
        <h3>Detailed Results</h3>
        <div className="resultados-resumen">
          <div className="resumen-item correcta">
            <span className="resumen-indicador"></span>
            <span className="resumen-texto">
              Correct: <strong>{conteos.correctas}</strong>
            </span>
          </div>
          <div className="resumen-item incorrecta">
            <span className="resumen-indicador"></span>
            <span className="resumen-texto">
              Incorrect: <strong>{conteos.incorrectas}</strong>
            </span>
          </div>
          <div className="resumen-item sin-responder">
            <span className="resumen-indicador"></span>
            <span className="resumen-texto">
              Not answered: <strong>{conteos.sinResponder}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="resultados-filtros">
        <div className="filtros-row-resultados">
          <div className="filtro-select-resultados">
            <label htmlFor="filtro-estado-resultados">Filter by status:</label>
            <select
              id="filtro-estado-resultados"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="select-filtro-resultados"
            >
              <option value="todos">All questions</option>
              <option value="correctas">Correct</option>
              <option value="incorrectas">Incorrect</option>
              <option value="sin_responder">Not answered</option>
            </select>
          </div>
          {filtroEstado !== 'todos' && (
            <button
              onClick={limpiarFiltros}
              className="btn-limpiar-filtros-resultados"
            >
              Clear filter
            </button>
          )}
        </div>
        <div className="filtros-contador-resultados">
          <span>
            Showing <strong>{preguntasFiltradas.length}</strong> of <strong>{preguntasOrden.length}</strong> questions
          </span>
        </div>
      </div>
      
      <div className="resultados-detallados-grid">
        {preguntasFiltradas.length === 0 ? (
          <div className="sin-resultados-resultados">
            <p>No questions found with the applied filter.</p>
            <button onClick={limpiarFiltros} className="btn-limpiar-filtros-resultados">
              Clear filter
            </button>
          </div>
        ) : (
          preguntasFiltradas.map((preguntaId) => {
          const pregunta = examen.preguntas.find(p => Number(p.id) === Number(preguntaId));
          if (!pregunta) return null;
          
          const respuestaUsuario = respuestasUsuario[preguntaId];
          const esCorrecta = esPreguntaCorrecta(preguntaId);
          const sinResponder = respuestaUsuario === undefined || respuestaUsuario === null;
          
          let estado = 'sin_responder';
          if (sinResponder) {
            estado = 'sin_responder';
          } else if (esCorrecta) {
            estado = 'correcta';
          } else {
            estado = 'incorrecta';
          }
          
          // Encontrar el índice original en preguntasOrden para el número de pregunta
          const indexOriginal = preguntasOrden.indexOf(preguntaId);
          const numeroPregunta = indexOriginal + 1;

            return (
            <div
              key={preguntaId}
              className={`resultado-card resultado-${estado}`}
              onClick={() => setPreguntaModal({ pregunta, preguntaId, numeroPregunta, respuestaUsuario, respuestaCorrecta: respuestasCorrectas[preguntaId], esCorrecta, sinResponder })}
            >
              <div className="resultado-numero">
                {numeroPregunta}
              </div>
              <div className="resultado-contenido">
                <div className="resultado-texto">
                  {pregunta.texto.replace(/\n/g, ' ').substring(0, 150)}
                  {pregunta.texto.replace(/\n/g, ' ').length > 150 && '...'}
                </div>
                <div className="resultado-categoria">
                  {pregunta.categoria?.nombre || 'General'}
                </div>
                <div className="resultado-estado-badge">
                  {sinResponder ? 'Not answered' : esCorrecta ? 'Correct' : 'Incorrect'}
                </div>
              </div>
              <div className="resultado-indicador">
                {sinResponder && <span className="indicador-sin-responder">?</span>}
                {!sinResponder && esCorrecta && <span className="indicador-correcta">✓</span>}
                {!sinResponder && !esCorrecta && <span className="indicador-incorrecta">✗</span>}
              </div>
            </div>
          );
          })
        )}
      </div>

      {/* Modal para mostrar pregunta detallada */}
      {preguntaModal && (
        <ModalPregunta
          pregunta={preguntaModal.pregunta}
          numeroPregunta={preguntaModal.numeroPregunta}
          respuestaUsuario={preguntaModal.respuestaUsuario}
          respuestaCorrecta={preguntaModal.respuestaCorrecta}
          esCorrecta={preguntaModal.esCorrecta}
          sinResponder={preguntaModal.sinResponder}
          onClose={() => setPreguntaModal(null)}
        />
      )}
    </div>
  );
}

export default ResultadosDetallados;

